import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getCurrentUser } from '../services/api';

interface UserData {
  _id: string;
  name: string;
  email: string;
  phone?: string;
  timezone?: string;
  theme?: 'light' | 'dark' | 'system';
  language?: 'pt' | 'en';
  avatar?: string;
  createdAt: string;
  updatedAt: string;
}

interface UserContextType {
  user: UserData | null;
  loading: boolean;
  error: string | null;
  refreshUser: () => Promise<void>;
  updateUser: (userData: Partial<UserData>) => void;
  logout: () => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshUser = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await getCurrentUser();
      const userData = response.data.data.user;
      
      setUser(userData);
    } catch (err: any) {
      console.error('Error loading user:', err);
      
      // Se o erro for 401 (não autorizado), limpar completamente
      if (err.response?.status === 401) {
        // Don't call logout() here as it might trigger re-renders
        // Just clear the user data
        setUser(null);
        setError('Não autorizado');
        localStorage.removeItem('token');
      } else {
        setError('Erro ao carregar dados do usuário');
      }
    } finally {
      setLoading(false);
    }
  };

  const updateUser = (userData: Partial<UserData>) => {
    setUser(prev => prev ? { ...prev, ...userData } : null);
  };

  const logout = () => {
    // Limpar todos os dados do usuário
    setUser(null);
    setError(null);
    setLoading(false);
    
    // Limpar token do localStorage
    localStorage.removeItem('token');
    
    // Limpar qualquer cache adicional que possa existir
    localStorage.removeItem('userCache');
    sessionStorage.clear();
    
    // Limpar todo o localStorage relacionado ao usuário
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.includes('user') || key.includes('project') || key.includes('auth'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    
    // Forçar um re-render para garantir que o estado seja atualizado
    window.dispatchEvent(new Event('storage'));
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      refreshUser();
    } else {
      setLoading(false);
    }
  }, []); // Remove dependencies to prevent re-running

  useEffect(() => {
    // Listen for logout events from API interceptor
    const handleLogoutEvent = () => {
      logout();
    };

    window.addEventListener('userLogout', handleLogoutEvent);
    return () => {
      window.removeEventListener('userLogout', handleLogoutEvent);
    };
  }, []); // Empty dependency array

  const value: UserContextType = {
    user,
    loading,
    error,
    refreshUser,
    updateUser,
    logout
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export default UserContext;